import { useEffect, useRef } from 'react';
import { FLOOR_DB } from '@/engine/spectrum';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';

/**
 * The master spectrum.
 *
 * Drawn on a canvas from an animation frame rather than from React state: the
 * display changes sixty times a second, and a component that re-rendered that
 * often would drag every sibling with it. The same reasoning as the playhead.
 *
 * It stops asking for frames when the transport is stopped, so an idle project
 * costs nothing.
 */

/** Top of the scale. Above this a mix is clipping and the bar is pinned. */
const TOP_DB = 6;
/** Labelled gridlines, at the frequencies people actually name. */
const AXIS_HZ = [50, 100, 200, 500, 1000, 2000, 5000, 10000];

export function Spectrum(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const transportState = useDaw((s) => s.transportState);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let frame = 0;
    let stopped = false;

    const draw = (): void => {
      if (stopped) return;
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
      }

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#0b0d11';
      ctx.fillRect(0, 0, width, height);

      const reading = getServices().engine.readSpectrum();

      // Horizontal gridlines every twelve decibels, labelled at the left.
      ctx.font = '9px ui-monospace, monospace';
      ctx.textBaseline = 'middle';
      for (let db = 0; db >= -96; db -= 12) {
        const y = yFor(db, height);
        ctx.fillStyle = db === 0 ? '#2b323c' : '#191d24';
        ctx.fillRect(0, Math.round(y) + 0.5, width, 1);
        ctx.fillStyle = '#4b5563';
        ctx.fillText(`${db}`, 3, y - 6);
      }

      if (reading) {
        const { levels, frequencies } = reading;
        const bandWidth = width / levels.length;

        for (let b = 0; b < levels.length; b++) {
          const level = Math.min(TOP_DB, levels[b]);
          if (level <= FLOOR_DB) continue;
          const y = yFor(level, height);
          const x = b * bandWidth;
          // Brighter towards the top, so a loud band reads as loud even in
          // peripheral vision.
          const heat = Math.min(1, Math.max(0, (level + 72) / 72));
          ctx.fillStyle = `rgba(${90 + heat * 130}, ${150 + heat * 70}, 255, ${0.35 + heat * 0.5})`;
          ctx.fillRect(x + 0.5, y, Math.max(1, bandWidth - 1), height - y);
        }

        // Frequency axis, labelled where the bands actually fall.
        ctx.fillStyle = '#4b5563';
        for (const hz of AXIS_HZ) {
          let nearest = 0;
          for (let b = 1; b < frequencies.length; b++) {
            if (Math.abs(Math.log2(frequencies[b] / hz)) < Math.abs(Math.log2(frequencies[nearest] / hz))) {
              nearest = b;
            }
          }
          const x = (nearest + 0.5) * bandWidth;
          ctx.fillText(hz >= 1000 ? `${hz / 1000}k` : `${hz}`, x - 6, height - 7);
        }
      } else {
        ctx.fillStyle = '#4b5563';
        ctx.fillText('Press play to see the spectrum', 10, height / 2);
      }

      ctx.restore();
      frame = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
    };
  }, [transportState]);

  return (
    <div className="spectrum">
      <canvas ref={canvasRef} className="spectrum__canvas" aria-label="Master spectrum" />
    </div>
  );
}

/** Where a level sits vertically: the top of the panel is TOP_DB, the bottom is the floor. */
function yFor(db: number, height: number): number {
  const span = TOP_DB - FLOOR_DB;
  const fraction = (TOP_DB - db) / span;
  return Math.min(height, Math.max(0, fraction * height));
}
